import { PresenceFilter, ReviewSearchParamsDto } from '@/contracts/marketplace/reviews';
import { Marketplace } from '@/enums/marketplace';
import type { RuntimeSupportedMarketplace } from '@/shared/marketplace';
import { isReviewStatusFilterValue } from '@/shared/review-status-filter';

const presenceFilterValues = new Set<PresenceFilter>(['with', 'without']);
const ratingValues = new Set<string>(['1', '2', '3', '4', '5']);
const platformValues = [Marketplace.Wb, Marketplace.Ozon] as const;
const platformValuesSet = new Set<string>(platformValues);

const trimToUndefined = (value: string | undefined): string | undefined => {
    if (typeof value !== 'string') {
        return undefined;
    }

    const nextValue = value.trim();
    return nextValue.length ? nextValue : undefined;
};

const normalizePresenceFilter = (value: PresenceFilter | undefined): PresenceFilter | undefined => {
    if (!value) {
        return undefined;
    }

    return presenceFilterValues.has(value) ? value : undefined;
};

const normalizeRating = (value: string | undefined): string | undefined => {
    const normalizedValue = trimToUndefined(value);
    if (!normalizedValue) {
        return undefined;
    }

    const uniqueSorted = Array.from(
        new Set(
            normalizedValue
                .split(',')
                .map((ratingValue) => ratingValue.trim())
                .filter((ratingValue) => ratingValues.has(ratingValue)),
        ),
    ).sort((leftValue, rightValue) => Number(leftValue) - Number(rightValue));

    return uniqueSorted.length ? uniqueSorted.join(',') : undefined;
};

const normalizePlatform = (value: string | undefined): string | undefined => {
    const normalizedValue = trimToUndefined(value);
    if (!normalizedValue) {
        return undefined;
    }

    const uniqueSorted = Array.from(
        new Set(
            normalizedValue
                .split(',')
                .map((platformValue) => platformValue.trim())
                .filter((platformValue) => platformValuesSet.has(platformValue)),
        ),
    ).sort(
        (leftValue, rightValue) =>
            platformValues.indexOf(leftValue as RuntimeSupportedMarketplace) -
            platformValues.indexOf(rightValue as RuntimeSupportedMarketplace),
    );

    return uniqueSorted.length ? uniqueSorted.join(',') : undefined;
};

export const normalizeReviewFilters = (filters: ReviewSearchParamsDto): ReviewSearchParamsDto => {
    const reviewText = trimToUndefined(filters.reviewText);
    const answerText = trimToUndefined(filters.answerText);
    const hasPhotos = normalizePresenceFilter(filters.hasPhotos);
    const hasAuthor = normalizePresenceFilter(filters.hasAuthor);
    const hasReviewText = normalizePresenceFilter(filters.hasReviewText);
    const hasAnswerText = normalizePresenceFilter(filters.hasAnswerText);
    const hasAiResponse = normalizePresenceFilter(filters.hasAiResponse);
    const rating = normalizeRating(filters.rating);
    const createdDate = trimToUndefined(filters.createdDate);
    const answerUpdatedDate = trimToUndefined(filters.answerUpdatedDate);
    const convertedStatus = trimToUndefined(filters.convertedStatus);
    const platform = normalizePlatform(filters.platform);
    const productId = trimToUndefined(filters.productId);
    const brandName = trimToUndefined(filters.brandName);

    return {
        ...(reviewText ? { reviewText } : {}),
        ...(answerText ? { answerText } : {}),
        ...(hasPhotos ? { hasPhotos } : {}),
        ...(hasAuthor ? { hasAuthor } : {}),
        ...(hasReviewText ? { hasReviewText } : {}),
        ...(hasAnswerText ? { hasAnswerText } : {}),
        ...(hasAiResponse ? { hasAiResponse } : {}),
        ...(rating ? { rating } : {}),
        ...(createdDate ? { createdDate } : {}),
        ...(answerUpdatedDate ? { answerUpdatedDate } : {}),
        ...(convertedStatus && isReviewStatusFilterValue(convertedStatus) ? { convertedStatus } : {}),
        ...(platform ? { platform } : {}),
        ...(productId ? { productId } : {}),
        ...(brandName ? { brandName } : {}),
    };
};

const comparableFilterKeys: Array<keyof ReviewSearchParamsDto> = [
    'reviewText',
    'answerText',
    'hasPhotos',
    'hasAuthor',
    'hasReviewText',
    'hasAnswerText',
    'hasAiResponse',
    'rating',
    'createdDate',
    'answerUpdatedDate',
    'convertedStatus',
    'platform',
    'productId',
    'brandName',
];

export const areReviewFiltersEquivalent = (left: ReviewSearchParamsDto, right: ReviewSearchParamsDto): boolean => {
    const normalizedLeft = normalizeReviewFilters(left);
    const normalizedRight = normalizeReviewFilters(right);

    return comparableFilterKeys.every((filterKey) => normalizedLeft[filterKey] === normalizedRight[filterKey]);
};
